import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import DashboardHeader from './components/DashboardHeader';
import StatusCard, { CrawlerResult } from './components/StatusCard';
import ActivityLog, { LogEntry } from './components/ActivityLog';
import StatsOverview from './components/StatsOverview';
import RecentNews from './components/RecentNews';
import NewsFormModal from './components/NewsFormModal';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

export default function DashboardPage() {
  const [petitionCount, setPetitionCount] = useState(0);
  const [petitionUpdatedAt, setPetitionUpdatedAt] = useState('');
  const [signerCount, setSignerCount] = useState(0);
  const [commentCount, setCommentCount] = useState(0);
  const [newsCount, setNewsCount] = useState(0);
  const [orgCount, setOrgCount] = useState(0);
  const [recentNews, setRecentNews] = useState<Array<{
    id: string;
    title: string;
    url: string;
    source: string;
    published_at: string;
    created_at: string;
  }>>([]);

  const [petitionRunning, setPetitionRunning] = useState(false);
  const [newsRunning, setNewsRunning] = useState(false);
  const [petitionResult, setPetitionResult] = useState<CrawlerResult | null>(null);
  const [newsResult, setNewsResult] = useState<CrawlerResult | null>(null);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // 수동 숫자 변경 상태
  const [isEditingCount, setIsEditingCount] = useState(false);
  const [editCountValue, setEditCountValue] = useState('');
  const [isSavingCount, setIsSavingCount] = useState(false);
  const [saveCountResult, setSaveCountResult] = useState<{ success: boolean; message: string } | null>(null);

  const [isNewsFormOpen, setIsNewsFormOpen] = useState(false);

  const addLog = useCallback((type: LogEntry['type'], message: string, success: boolean, details?: string) => {
    const entry: LogEntry = {
      id: crypto.randomUUID(),
      timestamp: new Date().toISOString(),
      type,
      message,
      success,
      details,
    };
    setLogs((prev) => [entry, ...prev]);
  }, []);

  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const fetchAllData = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const [petitionRes, signerRes, commentRes, newsCountRes, orgRes, newsListRes] = await Promise.all([
        supabase.from('petition_stats').select('count, updated_at').order('updated_at', { ascending: false }).limit(1).maybeSingle(),
        supabase.from('signers').select('*', { count: 'exact', head: true }),
        supabase.from('comments').select('*', { count: 'exact', head: true }),
        supabase.from('news_articles').select('*', { count: 'exact', head: true }),
        supabase.from('organizations').select('*', { count: 'exact', head: true }),
        supabase.from('news_articles').select('*').order('published_at', { ascending: false }).limit(20),
      ]);

      if (petitionRes.data) {
        setPetitionCount(petitionRes.data.count);
        setPetitionUpdatedAt(petitionRes.data.updated_at);
      }
      if (signerRes.count !== null) setSignerCount(signerRes.count);
      if (commentRes.count !== null) setCommentCount(commentRes.count);
      if (newsCountRes.count !== null) setNewsCount(newsCountRes.count);
      if (orgRes.count !== null) setOrgCount(orgRes.count);
      if (newsListRes.data) setRecentNews(newsListRes.data);

      addLog('info', '데이터 새로고침 완료', true, '모든 통계가 최신 상태로 업데이트됨');
    } catch (err) {
      addLog('error', '데이터 로드 실패', false, String(err));
    } finally {
      setIsRefreshing(false);
    }
  }, [addLog]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const runPetitionCrawler = async () => {
    setPetitionRunning(true);
    setPetitionResult(null);
    addLog('petition', '청원 크롤러 실행 시작', true, '국회 청원 페이지에서 동의 수를 가져오는 중...');

    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/petition-crawler`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        },
      });

      const data = await response.json();
      setPetitionResult(data);

      if (data.success) {
        setPetitionCount(data.count);
        setPetitionUpdatedAt(data.updated_at);
        addLog('petition', `청원 크롤링 성공: ${data.count}명`, true, data.message);
      } else {
        addLog('petition', '청원 크롤링 실패', false, data.error || '알 수 없는 오류');
      }
    } catch (err) {
      const errorResult: CrawlerResult = { success: false, error: String(err) };
      setPetitionResult(errorResult);
      addLog('petition', '청원 크롤러 오류', false, String(err));
    } finally {
      setPetitionRunning(false);
    }
  };

  const runNewsCrawler = async () => {
    setNewsRunning(true);
    setNewsResult(null);
    addLog('news', '뉴스 크롤러 실행 시작', true, '네이버 뉴스에서 관련 기사를 수집하는 중...');

    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/news-crawler?action=crawl`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        },
      });

      const data = await response.json();
      setNewsResult(data);

      if (data.success) {
        addLog('news', `뉴스 크롤링 성공: ${data.saved}건 저장`, true, `수집 ${data.fetched}건, 저장 ${data.saved}건, 중복 ${data.skipped}건`);
        // 뉴스 목록 새로고침
        const { data: freshNews } = await supabase
          .from('news_articles')
          .select('*')
          .order('published_at', { ascending: false })
          .limit(20);
        if (freshNews) {
          setRecentNews(freshNews);
          const { count } = await supabase.from('news_articles').select('*', { count: 'exact', head: true });
          if (count !== null) setNewsCount(count);
        }
      } else {
        addLog('news', '뉴스 크롤링 실패', false, data.error || '알 수 없는 오류');
      }
    } catch (err) {
      const errorResult: CrawlerResult = { success: false, error: String(err) };
      setNewsResult(errorResult);
      addLog('news', '뉴스 크롤러 오류', false, String(err));
    } finally {
      setNewsRunning(false);
    }
  };

  const runAllCrawlers = async () => {
    addLog('info', '전체 크롤러 일괄 실행', true, '청원 + 뉴스 크롤러를 동시에 실행합니다');
    await Promise.all([runPetitionCrawler(), runNewsCrawler()]);
  };

  const handleOpenEditCount = () => {
    setEditCountValue(String(petitionCount));
    setSaveCountResult(null);
    setIsEditingCount(true);
  };

  const handleSaveCount = async () => {
    const newCount = parseInt(editCountValue, 10);
    if (isNaN(newCount) || newCount < 0) {
      setSaveCountResult({ success: false, message: '올바른 숫자를 입력해주세요.' });
      return;
    }
    setIsSavingCount(true);
    setSaveCountResult(null);
    try {
      const { error: updateError } = await supabase
        .from('petition_stats')
        .update({
          count: newCount,
          updated_at: new Date().toISOString()
        })
        .eq('id', '4dc6fed5-0db8-4a63-bc6a-cbe25818c293');

      if (updateError) {
        throw new Error(`저장 실패: ${updateError.message}`);
      }

      setPetitionCount(newCount);
      setPetitionUpdatedAt(new Date().toISOString());
      addLog('petition', `청원 수 수동 변경: ${newCount.toLocaleString()}명`, true, '관리자가 직접 수정함');
      setSaveCountResult({ success: true, message: `${newCount.toLocaleString()}명으로 저장되었습니다.` });
      setTimeout(() => setIsEditingCount(false), 1200);
    } catch (err: any) {
      console.error('Save error:', err);
      setSaveCountResult({ success: false, message: err.message || String(err) });
      addLog('error', '청원 수 저장 실패', false, err.message || String(err));
    } finally {
      setIsSavingCount(false);
    }
  };

  const handleNewsSaved = async () => {
    addLog('news', '뉴스 수동 추가 완료', true, '관리자가 직접 뉴스를 추가함');
    const { data: freshNews } = await supabase
      .from('news_articles')
      .select('*')
      .order('published_at', { ascending: false })
      .limit(20);
    if (freshNews) {
      setRecentNews(freshNews);
      const { count } = await supabase.from('news_articles').select('*', { count: 'exact', head: true });
      if (count !== null) setNewsCount(count);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50/50" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>
      <DashboardHeader />

      {/* 뉴스 수동 입력 모달 */}
      <NewsFormModal
        isOpen={isNewsFormOpen}
        onClose={() => setIsNewsFormOpen(false)}
        onSaved={handleNewsSaved}
      />

      {/* 수동 숫자 변경 모달 */}
      {isEditingCount && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-50">
                <i className="ri-government-line text-xl text-amber-600"></i>
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">청원 수 수동 변경</h3>
                <p className="text-xs text-gray-400 mt-0.5">국회 청원 동의 수를 직접 입력하세요</p>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">청원 동의 수</label>
              <div className="relative">
                <input
                  type="number"
                  value={editCountValue}
                  onChange={(e) => setEditCountValue(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveCount()}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-lg font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent pr-12"
                  placeholder="숫자 입력"
                  min={0}
                  autoFocus
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-400 font-medium">명</span>
              </div>
            </div>

            {saveCountResult && (
              <div className={`flex items-center gap-2 rounded-lg px-3 py-2 mb-4 text-xs font-medium ${
                saveCountResult.success
                  ? 'bg-emerald-50 text-emerald-700'
                  : 'bg-red-50 text-red-600'
              }`}>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={saveCountResult.success ? 'ri-check-line' : 'ri-error-warning-line'}></i>
                </div>
                {saveCountResult.message}
              </div>
            )}

            <div className="flex gap-2">
              <button
                onClick={() => setIsEditingCount(false)}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                취소
              </button>
              <button
                onClick={handleSaveCount}
                disabled={isSavingCount}
                className={`flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition-colors whitespace-nowrap cursor-pointer ${
                  isSavingCount ? 'bg-amber-300 cursor-not-allowed' : 'bg-amber-500 hover:bg-amber-600'
                }`}
              >
                {isSavingCount ? (
                  <span className="flex items-center justify-center gap-1.5">
                    <i className="ri-loader-4-line animate-spin text-sm"></i>
                    저장 중...
                  </span>
                ) : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* 상단 액션 바 */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900">크롤러 관리</h2>
            <p className="text-xs text-gray-400 mt-0.5">크롤러를 실행하고 결과를 실시간으로 확인하세요</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsNewsFormOpen(true)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-teal-50 border border-teal-200 text-xs font-semibold text-teal-700 hover:bg-teal-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-add-line text-sm"></i>
              </div>
              뉴스 수동 입력
            </button>
            <button
              onClick={handleOpenEditCount}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs font-semibold text-amber-700 hover:bg-amber-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-edit-2-line text-sm"></i>
              </div>
              청원 수 수동 변경
            </button>
            <button
              onClick={fetchAllData}
              disabled={isRefreshing}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              <div className={`w-4 h-4 flex items-center justify-center ${isRefreshing ? 'animate-spin' : ''}`}>
                <i className="ri-refresh-line text-sm"></i>
              </div>
              새로고침
            </button>
            <button
              onClick={runAllCrawlers}
              disabled={petitionRunning || newsRunning}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                petitionRunning || newsRunning
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-red-600 text-white hover:bg-red-700 shadow-sm'
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-rocket-line text-sm"></i>
              </div>
              전체 실행
            </button>
          </div>
        </div>

        {/* 전체 현황 */}
        <div className="mb-6">
          <StatsOverview
            petitionCount={petitionCount}
            signerCount={signerCount}
            commentCount={commentCount}
            newsCount={newsCount}
            orgCount={orgCount}
          />
        </div>

        {/* 크롤러 카드 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <StatusCard
            title="청원 크롤러"
            icon="ri-government-line"
            iconBg="bg-amber-50"
            iconColor="text-amber-600"
            value={`${petitionCount.toLocaleString()}명`}
            subtitle="국회 청원 동의 수"
            lastUpdated={formatDateTime(petitionUpdatedAt)}
            onRun={runPetitionCrawler}
            isRunning={petitionRunning}
            result={petitionResult}
          />
          <StatusCard
            title="뉴스 크롤러"
            icon="ri-newspaper-line"
            iconBg="bg-teal-50"
            iconColor="text-teal-600"
            value={`${newsCount.toLocaleString()}건`}
            subtitle="수집된 뉴스 기사"
            lastUpdated={recentNews.length > 0 ? formatDateTime(recentNews[0].created_at) : '-'}
            onRun={runNewsCrawler}
            isRunning={newsRunning}
            result={newsResult}
          />
        </div>

        {/* 스케줄 정보 */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-50">
              <i className="ri-calendar-schedule-line text-lg text-gray-600"></i>
            </div>
            <h2 className="text-sm font-bold text-gray-900">자동 실행 스케줄</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex items-center gap-3 bg-amber-50/50 rounded-xl p-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-100">
                <i className="ri-time-line text-lg text-amber-600"></i>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-800">청원 크롤러</p>
                <p className="text-xs text-gray-400 mt-0.5">매일 오후 3:00 (KST) 자동 실행</p>
              </div>
              <div className="ml-auto flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></div>
                <span className="text-xs font-medium text-emerald-600">활성</span>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-teal-50/50 rounded-xl p-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-teal-100">
                <i className="ri-time-line text-lg text-teal-600"></i>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-800">뉴스 크롤러</p>
                <p className="text-xs text-gray-400 mt-0.5">수동 실행 전용</p>
              </div>
              <div className="ml-auto flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-gray-300"></div>
                <span className="text-xs font-medium text-gray-400">수동</span>
              </div>
            </div>
          </div>
        </div>

        {/* 하단 2열: 실행 기록 + 최근 뉴스 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ActivityLog logs={logs} />
          <RecentNews articles={recentNews} />
        </div>
      </main>
    </div>
  );
}
